import type { ConfirmationCardField, ConfirmationCardContextItem } from './confirmation.card.component';

/**
 * Pure view-model builders for write-tool approval cards.
 *
 * These were originally private members of AgentMessageListComponent. They are extracted here so
 * BOTH the inline message list AND the chat container's composer overlay can build the identical
 * card view-model from a tool part's input — the overlay now owns the single pending approval card
 * while the message list keeps the same maps for any non-overlay rendering. Keeping them as pure
 * functions (no component state) guarantees the two surfaces stay byte-for-byte identical.
 */

// ─── Title templates for approval cards (use {key} for substitution) ─────
const TOOL_TITLE_TEMPLATES: Record<string, string> = {
  updateJobDescription: 'Update Job {jobNumber}',
  addJobActivityNote: 'Add Note to Job {jobNumber}',
  updateStageOrder: 'Update Stages for Job {jobNumber}',
  updateStageStartDate: 'Update Stage Start Date for Job {jobNumber}',
  updateStageDueDate: 'Update Stage Due Date for Job {jobNumber}',
  addLineItemsToJob: 'Add Items to Job {jobNumber}',
  createCrmAccount: 'Create Account {name}',
  changeWorkOrderStatus: 'Change Stage Status',
  renameStage: 'Rename Stage',
  renameJobOrStage: 'Rename {target}',
  assignStageOwner: 'Assign Stage Owner',
  updateStageCategory: 'Change Stage Category',
  assignStageSubcontractor: 'Assign Stage Subcontractor',
  removeStageSubcontractor: 'Remove Stage Subcontractor',
  assignSchedulableResource: 'Assign Stage Resource',
  removeSchedulableResource: 'Remove Stage Resource',
  updateJobPriority: 'Update Priority for {jobNumber}',
  updateJobType: 'Update Type for {jobNumber}',
  updateJobStartDate: 'Update Start Date for {jobNumber}',
  updateJobDueDate: 'Update Due Date for {jobNumber}',
  updateJobLabels: 'Update Labels on {jobNumber}',
  updateJobRefs: 'Update Reference for {jobNumber}',
  createTask: 'Create Task on {jobNumber}',
  updateTask: 'Update Task: {taskName}',
  completeTask: 'Complete Task: {taskName}',
  deleteTask: 'Delete Task: {taskName}',
  assignTask: 'Assign Task: {taskName}',
};

// ─── Field label mapping for approval cards (excludes fields shown in title) ─────
const TOOL_APPROVAL_FIELDS: Record<string, Record<string, string>> = {
  updateJobDescription: { newDescription: 'New Description' },
  addJobActivityNote: { note: 'Note' },
  updateStageOrder: { orderedStageNames: 'New Order' },
  updateStageStartDate: { date: 'Start Date' },
  updateStageDueDate: { date: 'Due Date' },
  addLineItemsToJob: { items: 'Items' },
  changeWorkOrderStatus: { newStatus: 'New Status' },
  renameStage: { newName: 'New Name' },
  renameJobOrStage: { newName: 'New Name' },
  assignStageOwner: { selectedName: 'Owner' },
  updateStageCategory: { selectedName: 'Category' },
  assignStageSubcontractor: { selectedName: 'Subcontractor' },
  removeStageSubcontractor: { selectedName: 'Subcontractor' },
  // selectedNames (plural) is legacy: pre-split cards persisted in old threads carry it, and
  // restored cards render from their stored input forever — keep both keys mapped.
  assignSchedulableResource: { selectedName: 'Resource', selectedNames: 'Resources' },
  removeSchedulableResource: { selectedName: 'Resource' },
  updateJobPriority: { selectedName: 'New Priority' },
  updateJobType: { selectedName: 'New Type' },
  updateJobStartDate: { startDate: 'Start Date' },
  updateJobDueDate: { dueDate: 'Due Date' },
  updateJobLabels: { op: 'Operation', selectedNames: 'Labels' },
  updateJobRefs: { customerPO: 'Customer PO', customerWO: 'Customer WO' },
  createTask: { name: 'Task Name', description: 'Description', dueDate: 'Due Date', target: 'Target', selectedName: 'Assignee', mandatory: 'Mandatory', beforePhotoEnabled: 'Before Photo Required', duringPhotoEnabled: 'During Photo Required', afterPhotoEnabled: 'After Photo Required' },
  updateTask: { name: 'New Name', description: 'New Description', dueDate: 'New Due Date', target: 'New Target', mandatory: 'New Mandatory', beforePhotoEnabled: 'Before Photo Required', duringPhotoEnabled: 'During Photo Required', afterPhotoEnabled: 'After Photo Required' },
  completeTask: {},
  deleteTask: {},
  assignTask: { selectedName: 'Assignee' },
};

// ─── Maps approval field keys to their corresponding previous/current value keys in tool input ─────
const TOOL_PREVIOUS_VALUE_FIELDS: Record<string, Record<string, string[]>> = {
  updateJobDescription: { newDescription: ['previousValue'] },
  changeWorkOrderStatus: { newStatus: ['previousValue'] },
  renameStage: { newName: ['previousValue'] },
  renameJobOrStage: { newName: ['previousValue'] },
  assignStageOwner: { selectedName: ['previousValue'] },
  updateStageCategory: { selectedName: ['previousValue'] },
  assignStageSubcontractor: { selectedName: ['previousValue'] },
  assignSchedulableResource: { selectedName: ['previousValue'], selectedNames: ['previousValue'] },
  updateJobPriority: { selectedName: ['previousValue'] },
  updateJobType: { selectedName: ['previousValue'] },
  updateJobStartDate: { startDate: ['previousValue'] },
  updateJobDueDate: { dueDate: ['previousValue'] },
  updateStageStartDate: { date: ['previousValue'] },
  updateStageDueDate: { date: ['previousValue'] },
  updateJobLabels: { selectedNames: ['previousValue'] },
  updateJobRefs: { customerPO: ['previousValue'], customerWO: ['previousValue'] },
  // updateTask: the model passes a single previousValue for the PRIMARY field being changed,
  // so map every changeable field key to it — whichever field appears renders before→after.
  updateTask: { name: ['previousValue'], description: ['previousValue'], dueDate: ['previousValue'], target: ['previousValue'], mandatory: ['previousValue'], beforePhotoEnabled: ['previousValue'], duringPhotoEnabled: ['previousValue'], afterPhotoEnabled: ['previousValue'] },
  assignTask: { selectedName: ['previousValue'] },
};

// ─── Fields to extract as reasoning text (checked in order, first match wins) ─────
const TOOL_REASONING_FIELDS: Record<string, string[]> = {
  updateJobDescription: ['reasoning'],
  updateStageOrder: ['reasoning'],
  renameStage: ['reasoning'],
  renameJobOrStage: ['reasoning'],
  changeWorkOrderStatus: ['reasoning', 'note'],
  assignStageOwner: ['reasoning'],
  updateStageCategory: ['reasoning'],
  assignStageSubcontractor: ['reasoning'],
  removeStageSubcontractor: ['reasoning'],
  assignSchedulableResource: ['reasoning'],
  removeSchedulableResource: ['reasoning'],
  updateJobPriority: ['reasoning'],
  updateJobType: ['reasoning'],
  updateJobStartDate: ['reasoning'],
  updateJobDueDate: ['reasoning'],
  updateStageStartDate: ['reasoning'],
  updateStageDueDate: ['reasoning'],
  updateJobLabels: ['reasoning'],
  updateJobRefs: ['reasoning'],
  createTask: ['reasoning'],
  updateTask: ['reasoning'],
  completeTask: ['reasoning'],
  deleteTask: ['reasoning'],
  assignTask: ['reasoning'],
};

/** Build the approval card title from the tool name template and its input args. */
export function buildToolTitle(rawName: string, inputData: unknown): string {

  const template = TOOL_TITLE_TEMPLATES[rawName];
  if (!template) return rawName.replace(/([A-Z])/g, ' $1').trim();

  const args = (inputData && typeof inputData === 'object') ? inputData as Record<string, unknown> : {};
  const result = template.replace(/\{(\w+)\}/g, (_, key) => {
    const value = args[key];
    return value != null && value !== '' ? String(value) : '';
  });

  // Clean up any trailing placeholder residue + a dangling separator colon (e.g., "Update Task: " -> "Update Task")
  return result.replace(/[\s:]+$/, '').trim();
}

/** Build the labelled field rows for the approval card from the tool input. */
export function buildConfirmationCardFields(rawName: string, inputData: unknown): ConfirmationCardField[] {

  if (!inputData || typeof inputData !== 'object') return [];
  const data = inputData as Record<string, unknown>;
  const fieldMap = TOOL_APPROVAL_FIELDS[rawName];
  const previousMap = TOOL_PREVIOUS_VALUE_FIELDS[rawName];
  const entries = Object.entries(data);
  return entries
    .filter(([key]) => !fieldMap || key in fieldMap)
    .slice(0, 10) // safety cap on card length; createTask maps 9 fields, so 6 silently dropped its photo toggles
    .map(([key, value]) => {
      const strValue = formatFieldValue(rawName, value);
      // A remove call's selectedName is the resource whose membership is ending, not the
      // proposed after-value. Model the targeted membership directly so a removal never
      // renders as a no-op (or falsely implies that the stage's other resources are removed).
      if ((rawName === 'removeSchedulableResource' || rawName === 'removeStageSubcontractor') && key === 'selectedName') {
        return { key, label: fieldMap?.[key] ?? key, value: '(none)', previousValue: strValue, isLong: strValue.length > 80 || strValue.includes('\n') };
      }
      // A label add or remove touches only the labels picked; the job's other labels stay. Show those labels
      // arriving or leaving, so a remove never reads as a no-op and an add never reads as a replace.
      if (rawName === 'updateJobLabels' && key === 'selectedNames' && data.op === 'add') {
        return { key, label: fieldMap?.[key] ?? key, value: strValue, previousValue: '(none)', isLong: strValue.length > 80 || strValue.includes('\n') };
      }
      if (rawName === 'updateJobLabels' && key === 'selectedNames' && data.op === 'remove') {
        return { key, label: fieldMap?.[key] ?? key, value: '(none)', previousValue: strValue, isLong: strValue.length > 80 || strValue.includes('\n') };
      }
      const previousValue = resolvePreviousValue(key, data, previousMap, rawName);
      return { key, label: fieldMap?.[key] ?? key, value: strValue, previousValue, isLong: strValue.length > 80 || strValue.includes('\n') };
    });
}

/** Format a single tool-input value into a human-readable card string. */
function formatApprovalValue(value: unknown): string {

  if (value === null || value === undefined || value === '') return '(none)';
  // '__CLEAR__' is the internal sentinel a write tool must pass to clear a field (a literal is
  // required because z.string().nullable() serializes to an unsatisfiable schema). Render it as
  // plain words so the keyword never leaks onto the approval card.
  if (value === '__CLEAR__') return 'Cleared';
  // Booleans (e.g. createTask/updateTask toggles: mandatory, beforePhotoEnabled, duringPhotoEnabled,
  // afterPhotoEnabled) render as Yes/No instead of falling through to String() -> "true"/"false".
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    const values = value.map((item) => {
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        // Named items render as a human line ("2 × PLU06200 — description") and
        // hide their remaining raw keys (IDs etc.) from the approval card.
        if ('name' in record) {
          const qty = typeof record.quantity === 'number' ? `${record.quantity} × ` : '';
          const desc = typeof record.description === 'string' && record.description.trim() ? ` — ${record.description.trim()}` : '';
          return `${qty}${String(record.name ?? '')}${desc}`;
        }
        return Object.entries(record).map(([key, val]) => `${key}: ${formatApprovalValue(val)}`).join(', ');
      }
      return String(item ?? '');
    }).filter(Boolean);
    return values.length > 0 ? values.join(', ') : '(none)';
  }
  if (value && typeof value === 'object' && 'name' in value) {
    return String((value as Record<string, unknown>).name ?? '');
  }
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

// The four date tools whose card values are ISO date strings. Their values (and previousValue) are
// formatted to a friendly, consistent LOCAL date/time so the before→after diff reads cleanly instead
// of raw ISO in mismatched timezones (e.g. a UTC "before" next to a +10:00 "after").
const DATE_TOOL_NAMES = new Set(['updateJobStartDate', 'updateJobDueDate', 'updateStageStartDate', 'updateStageDueDate']);

/** Format a card field value: friendly local date/time for the date tools, generic formatting otherwise. */
function formatFieldValue(rawName: string, value: unknown): string {

  if (DATE_TOOL_NAMES.has(rawName) && typeof value === 'string' && value && value !== '__CLEAR__') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    }
  }
  return formatApprovalValue(value);
}

/** Resolve a field's previous value (for the before→after diff) from the tool input. */
function resolvePreviousValue(fieldKey: string, data: Record<string, unknown>, previousMap: Record<string, string[]> | undefined, rawName: string): string | undefined {

  const candidates = previousMap?.[fieldKey];
  if (!candidates) return undefined;
  for (const candidate of candidates) {
    const val = data[candidate];
    if (val === undefined) continue;
    return formatFieldValue(rawName, val);
  }
  return undefined;
}

/** Build the context subtitle (job number · stage name) shown under the card title. */
export function buildConfirmationCardContext(rawName: string, inputData: unknown): ConfirmationCardContextItem[] {

  if (!inputData || typeof inputData !== 'object') return [];
  const data = inputData as Record<string, unknown>;

  const jobNumber = data['jobNumber'] as string | undefined;
  const stageName = data['stageName'] as string | undefined;

  const parts: string[] = [];
  if (jobNumber) parts.push(jobNumber);
  if (stageName) parts.push(stageName);

  if (parts.length === 0) return [];
  return [{ label: parts.join(' · ') }];
}

/** Extract the model's reasoning text (if any) for the approval card's rationale line. */
export function extractToolReasoning(rawName: string, inputData: unknown): string {

  if (!inputData || typeof inputData !== 'object') return '';
  const data = inputData as Record<string, unknown>;
  const candidates = TOOL_REASONING_FIELDS[rawName];
  if (!candidates) return '';
  for (const key of candidates) {
    const val = data[key];
    if (typeof val === 'string' && val.trim()) return val.trim();
  }
  return '';
}
